import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import {
  buildDailyFeedTotals,
  FeedChartEntry,
  feedRegistrationDayInstant,
  formatFeedAmount
} from '../../models/feed-registration';

const FEED_COLORS = ['#16a34a', '#2563eb', '#d97706', '#9333ea', '#db2777', '#0d9488', '#dc2626', '#4f46e5'];

export interface FeedDailyChartProps {
  entries: FeedChartEntry[];
  title: string;
  /** The tooltip's line under a day that got more than one feed, given the day's formatted sum. */
  formatDayTotal: (amount: string) => string;
}

interface ChartPoint {
  /** `yyyy-MM-dd`. */
  day: string;
  totalGrams: number;
  [feedKey: string]: string | number;
}

const feedColor = (index: number): string =>
  FEED_COLORS[index] ?? `hsl(${Math.round((index * 137.508) % 360)} 65% 40%)`;

/** Feed per day as stacked bars, one segment per feed. */
const FeedDailyChart: React.FC<FeedDailyChartProps> = ({ entries, title, formatDayTotal }) => {
  const { t } = useTranslation();
  const { formatDate } = useFarmDateTime();

  const dailyTotals = React.useMemo(() => buildDailyFeedTotals(entries), [entries]);
  const feedNames = React.useMemo(
    () =>
      [...new Set(entries.map((entry) => entry.feedName.trim() || t('f-feed-reg-name')))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [entries, t]
  );
  const feedSeries = React.useMemo(
    () => feedNames.map((name, index) => ({ name, key: `feed${index}`, color: feedColor(index) })),
    [feedNames]
  );

  // All series share one axis unit. The largest daily total decides whether it reads in g or kg.
  const maxGrams = Math.max(0, ...dailyTotals.map((point) => point.grams));
  const inKilograms = maxGrams >= 1000;
  const points = React.useMemo(() => {
    const byDay = new Map(dailyTotals.map(({ day, grams }) => [day, { day, totalGrams: grams } as ChartPoint]));
    const keyByName = new Map(feedSeries.map((feed) => [feed.name, feed.key]));
    for (const point of byDay.values()) {
      for (const feed of feedSeries) {
        point[feed.key] = 0;
      }
    }

    for (const entry of entries) {
      const point = byDay.get(entry.date);
      const key = keyByName.get(entry.feedName.trim() || t('f-feed-reg-name'));
      if (point && key) {
        point[key] = Number(point[key] ?? 0) + (inKilograms ? entry.grams / 1000 : entry.grams);
      }
    }

    return dailyTotals.map(({ day }) => byDay.get(day)!);
  }, [dailyTotals, entries, feedSeries, inKilograms, t]);

  if (points.length === 0) return null;

  const axisLabel = inKilograms ? t('f-feed-reg-unit-kg') : t('f-feed-reg-unit-g');

  return (
    <div className="px-4 pt-2 pb-4">
      <p className="m-0 mb-2 text-xs font-medium text-gray-700">
        {title} <span className="font-normal text-gray-500">({axisLabel})</span>
      </p>

      <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1" aria-label={t('f-feed-reg-name')}>
        {feedSeries.map((feed) => (
          <span key={feed.key} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: feed.color }} />
            {feed.name}
          </span>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={points} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />

          <XAxis
            dataKey="day"
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
            tickMargin={8}
            minTickGap={24}
            // A calendar day has no instant to convert, so the label cannot shift with timezone.
            tickFormatter={(day: string) => `${day.slice(8, 10)}/${day.slice(5, 7)}`}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
            width={44}
            tickFormatter={(value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          />

          <Tooltip
            isAnimationActive={false}
            cursor={{ fill: '#f3f4f6' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;

              const point = payload[0].payload as ChartPoint;
              // A quiet day has no bar, so hovering its slot has nothing to explain.
              if (point.totalGrams === 0) return null;
              const feedsOnDay = feedSeries.filter((feed) => Number(point[feed.key] ?? 0) > 0);

              return (
                <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
                  <p className="m-0 mb-1 font-medium text-gray-700">
                    {formatDate(feedRegistrationDayInstant(point.day))}
                  </p>
                  {feedsOnDay.map((feed) => (
                    <div key={feed.key} className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: feed.color }} />
                      <span className="text-gray-600">{feed.name}</span>
                      <span className="ml-auto font-medium text-gray-900">
                        {formatFeedAmount(Number(point[feed.key]) * (inKilograms ? 1000 : 1))}
                      </span>
                    </div>
                  ))}
                  {feedsOnDay.length > 1 && (
                    <p className="m-0 mt-1 border-t border-gray-100 pt-1 font-medium text-gray-900">
                      {formatDayTotal(formatFeedAmount(point.totalGrams))}
                    </p>
                  )}
                </div>
              );
            }}
          />

          {feedSeries.map((feed) => (
            <Bar
              key={feed.key}
              dataKey={feed.key}
              stackId="feed"
              fill={feed.color}
              maxBarSize={32}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default FeedDailyChart;
